const SCROLL_ANIMATION_TRIGGER_CLASSNAME = "animate-scroll-trigger";
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = "animate-scroll-trigger--offscreen";
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = "animate-scroll-trigger--zoom-in";
const SCROLL_ANIMATION_CANCEL_CLASSNAME = "animate-scroll-trigger--cancel";

/**
 * Handles intersection events for a set of observed elements, toggling animation classes
 * and setting animation order for cascading effects.
 *
 * @param {IntersectionObserverEntry[]} elements - Array of intersection observer entries.
 * @param {IntersectionObserver} observer - The IntersectionObserver instance observing the elements.
 */
function onIntersection(elements, observer) {
	for (const [index, entry] of elements.entries()) {
		if (entry.isIntersecting) {
			/** @type {HTMLElement} */
			const elementTarget = entry.target;

			if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
				elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
				if (Object.hasOwn(elementTarget.dataset, "cascade"))
					elementTarget.setAttribute("style", `--animation-order: ${index};`);
			}
			observer.unobserve(elementTarget);
		} else {
			entry.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
			entry.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
		}
	}
}

/**
 * @param {Element | Document | EventTarget} [rootElement=document]
 * @param {boolean} [isDesignModeEvent=false]
 */
function initializeScrollAnimationTrigger(rootElement = document, isDesignModeEvent = false) {
	// eslint-disable-next-line unicorn/prefer-query-selector
	const animationTriggerElements = rootElement.getElementsByClassName(
		SCROLL_ANIMATION_TRIGGER_CLASSNAME,
	);

	if (isDesignModeEvent) {
		for (const element of animationTriggerElements) {
			element.classList.add("animate-scroll-trigger--design-mode");
		}
		return;
	}

	const animationObserver = new IntersectionObserver(onIntersection, {
		rootMargin: "0px 0px -50px 0px",
	});

	for (const item of animationTriggerElements) animationObserver.observe(item);
}

/** @type {Set<HTMLElement>} */
const zoomElements = new Set();

/** @type {Set<HTMLElement>} */
const visibleZoomElements = new Set();

/** @type {WeakMap<Element, { offsetTop: number, height: number }>} */
const elementDimensions = new WeakMap();

const scaleAmount = 0.2 / 100;

/** @type {IntersectionObserver | null} */
let sharedZoomObserver = null;

let scrollListenerAttached = false;
let resizeListenerAttached = false;

/**
 * Cache dimensions for all zoom elements.
 * Batches layout reads to avoid forced reflows.
 */
function cacheAllDimensions() {
	// Read phase - batch all layout reads first
	const measurements = [];
	const scrollY = window.scrollY;
	for (const element of zoomElements) {
		measurements.push({
			element,
			rect: element.getBoundingClientRect(),
			height: element.offsetHeight,
		});
	}
	// Write phase - update cache
	for (const { element, rect, height } of measurements) {
		elementDimensions.set(element, {
			offsetTop: rect.top + scrollY,
			height,
		});
	}
}

/** @type {number | null} */
let rafId = null;

/**
 * Update zoom ratio for all visible elements - single scroll handler using rAF
 */
function updateVisibleElements() {
	if (rafId) return;
	rafId = requestAnimationFrame(() => {
		rafId = null;
		const scrollY = window.scrollY;
		const viewportHeight = window.innerHeight;
		for (const element of visibleZoomElements) {
			element.style.setProperty(
				"--zoom-in-ratio",
				String(1 + scaleAmount * percentageSeenCached(element, scrollY, viewportHeight)),
			);
		}
	});
}

/**
 * Debounced resize handler
 */
let resizeTimeout;
function handleResize() {
	clearTimeout(resizeTimeout);
	resizeTimeout = setTimeout(cacheAllDimensions, 150);
}

/**
 * Initializes a scroll-based zoom-in animation trigger on elements with the specified trigger class.
 * Elements will scale based on their visibility percentage in the viewport as the user scrolls.
 * Animation is disabled if the user prefers reduced motion.
 *
 * @param {Document|HTMLElement} [rootElement=document] - The root element to search for trigger elements within.
 */
function initializeScrollZoomAnimationTrigger(rootElement = document) {
	if (globalThis.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

	// eslint-disable-next-line unicorn/prefer-query-selector
	const animationTriggerElements = rootElement.getElementsByClassName(
		SCROLL_ZOOM_IN_TRIGGER_CLASSNAME,
	);

	if (animationTriggerElements.length === 0) return;

	// Create shared observer once
	if (!sharedZoomObserver) {
		sharedZoomObserver = new IntersectionObserver((entries) => {
			for (const entry of entries) {
				if (entry.isIntersecting) {
					visibleZoomElements.add(/** @type {HTMLElement} */ (entry.target));
				} else {
					visibleZoomElements.delete(/** @type {HTMLElement} */ (entry.target));
				}
			}
		});
	}

	// Add new elements to tracking
	for (const element of animationTriggerElements) {
		if (zoomElements.has(element)) continue;

		zoomElements.add(element);
		sharedZoomObserver.observe(element);

		// Cache initial dimensions
		const rect = element.getBoundingClientRect();
		elementDimensions.set(element, {
			offsetTop: rect.top + window.scrollY,
			height: element.offsetHeight,
		});

		// Set initial zoom ratio
		element.style.setProperty(
			"--zoom-in-ratio",
			String(1 + scaleAmount * percentageSeenCached(element, window.scrollY, window.innerHeight)),
		);
	}

	// Attach single scroll listener
	if (!scrollListenerAttached) {
		scrollListenerAttached = true;
		window.addEventListener("scroll", updateVisibleElements, { passive: true });
	}

	// Attach single resize listener
	if (!resizeListenerAttached) {
		resizeListenerAttached = true;
		window.addEventListener("resize", handleResize, { passive: true });
	}
}

/**
 * Calculates the percentage of a given DOM element that has been seen (scrolled into view) in the viewport.
 * Uses cached dimensions to avoid forced reflows during scroll.
 *
 * @param {HTMLElement} element - The DOM element to check visibility for.
 * @param {number} scrollY - Current scroll position.
 * @param {number} viewportHeight - Current viewport height.
 * @returns {number} The percentage (0 to 100) of the element that has been seen in the viewport.
 */
function percentageSeenCached(element, scrollY, viewportHeight) {
	const cached = elementDimensions.get(element);

	if (!cached) return 0;

	const { offsetTop: elementPositionY, height: elementHeight } = cached;

	if (elementPositionY > scrollY + viewportHeight) {
		// If we haven't reached the image yet
		return 0;
	} else if (elementPositionY + elementHeight < scrollY) {
		// If we've completely scrolled past the image
		return 100;
	}

	// When the image is in the viewport
	const distance = scrollY + viewportHeight - elementPositionY;
	const percentage = distance / ((viewportHeight + elementHeight) / 100);
	return Math.round(percentage);
}

// Use requestIdleCallback for non-critical initialization, fallback to DOMContentLoaded
if (document.readyState === "loading") {
	document.addEventListener(
		"DOMContentLoaded",
		() => {
			initializeScrollAnimationTrigger();
			initializeScrollZoomAnimationTrigger();
			setupMutationObserver();
		},
		{ once: true },
	);
} else {
	// DOM already loaded
	initializeScrollAnimationTrigger();
	initializeScrollZoomAnimationTrigger();
	setupMutationObserver();
}

/**
 * Set up mutation observer for dynamically added elements.
 * Debounces callbacks to avoid excessive initialization on rapid DOM changes.
 */
function setupMutationObserver() {
	let mutationTimeout;

	const mutationObserver = new MutationObserver((mutations) => {
		// Quick check if any mutations contain potential animation elements
		let hasRelevantNodes = false;
		outer: for (const mutation of mutations) {
			for (const node of mutation.addedNodes) {
				if (node instanceof Element) {
					hasRelevantNodes = true;
					break outer;
				}
			}
		}

		if (!hasRelevantNodes) return;

		// Debounce the initialization
		clearTimeout(mutationTimeout);
		mutationTimeout = setTimeout(() => {
			initializeScrollAnimationTrigger();
			initializeScrollZoomAnimationTrigger();
		}, 100);
	});

	mutationObserver.observe(document.body, {
		childList: true,
		subtree: true,
	});
}

if (globalThis.Shopify?.designMode) {
	document.addEventListener("shopify:section:load", (event) =>
		initializeScrollAnimationTrigger(event.target, true),
	);
	document.addEventListener("shopify:section:reorder", () =>
		initializeScrollAnimationTrigger(document, true),
	);
}
